import React, { useCallback, useMemo } from 'react';

import { DownloadIcon, TerminalIcon } from '@primer/octicons-react';
import { Tooltip } from '@primer/react';

import byteSize from 'byte-size';

import AlternatingRowDataTable from '../../../common/DataTable/AlternatingRowDataTable';
import Button from '../../../common/Button/Button';
import ClipboardLabel from '../../../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../../../common/Labels/CodeFormatLabel';
import Icon from '../../../common/icons/Icon';
import Label from '../../../common/Labels/Label';

import TrashFillIcon from '../../../common/icons/TrashFillIcon';

import OrchestrationStatusBadge from '../../../orchestration/OrchestrationStatusBadge/OrchestrationStatusBadge';

import { formatDateWithTimeSeparator } from '../../../../utils/date_utils';
import { getThemeColor } from '../../../../utils/theme-utils';

const transformOrchestrationsToTableRows = (orchestrations = []) =>
  orchestrations?.map(orchestrationObj => {
    const {
      orchestration: { id, name, file_size, sha, created_at, created_by, status },
      workspace,
      tasks,
    } = orchestrationObj;

    const numActiveTasks = tasks?.filter(({ status: taskRunStatus }) => {
      const statusStr = taskRunStatus.toLowerCase();
      return statusStr === 'started' || statusStr === 'running';
    }).length;

    return {
      id,
      name,
      status,
      workspace_id: workspace.id,
      workspace_name: workspace.name,
      workspace: {
        id: workspace.id,
        name: workspace.name,
        owner: workspace.created_by,
      },
      created_at,
      created_by,
      numberOfActiveTasks: numActiveTasks,
      storageSize: file_size,
      sha,
    };
  });

const truncateText = (inputText, truncateAt = 8) => {
  if (inputText.length < truncateAt + 1) {
    return inputText;
  }
  return `${inputText.substring(0, truncateAt)}...`;
};

const CellSubLabel = ({ children, ...rest }) => (
  <Label style={{ color: getThemeColor('info') }} {...rest}>
    {children}
  </Label>
);

const NameCell = ({ value }) => {
  const truncatedName = truncateText(value || '--', 20);

  return (
    <div className="flex flex-col p-2">
      <Label>{truncatedName}</Label>
    </div>
  );
};

const WorkspaceCell = ({ value, row }) => {
  const workspace = row?.original?.workspace;
  const truncatedName = truncateText(value || '--', 30);
  const owner = workspace?.owner;
  const truncatedOwner = truncateText(owner || '--', 20);
  return (
    <div className="flex flex-col">
      <Label>{truncatedName}</Label>
      {owner?.length > 0 && (
        <CellSubLabel className="mt-1 break-words">{truncatedOwner}</CellSubLabel>
      )}
    </div>
  );
};

const FileHashCell = ({ value }) => {
  const truncatedHash = truncateText(value || '--', 20);
  return (
    <div className="flex">
      <ClipboardLabel
        iconSize={16}
        labelValue={value}
        displayLabel={truncatedHash}
        LabelComponent={CodeFormatLabel}
        showClipboardIconOnHover
      />
    </div>
  );
};

const TasksCell = ({ value }) => (
  <div className="flex flex-col">
    <Label>{value || 0}</Label>
  </div>
);

const StorageSizeCell = ({ value }) => {
  const storage = value || 0;
  const storageData = byteSize(storage);
  const storageLabel = `${storageData.value} ${storageData.unit}`;
  return (
    <div className="flex flex-col">
      <Label>{value ? storageLabel : '--'}</Label>
    </div>
  );
};

const StatusCell = ({ value }) => <OrchestrationStatusBadge status={value} />;

const CreatedAtCell = ({ value, row }) => {
  if (value === null) {
    return <Label>--</Label>;
  }

  const createdBy = row?.original?.created_by;
  const truncatedCreatedBy = truncateText(createdBy || '--', 20);

  return (
    <div className="flex flex-col">
      <Label>{formatDateWithTimeSeparator(new Date(value).toISOString())}</Label>
      {createdBy && <CellSubLabel className="mt-1 break-words">{truncatedCreatedBy}</CellSubLabel>}
    </div>
  );
};

const ActionCellRenderer = ({ row, meta = {} }) => {
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  };

  const onFirstButtonClick = useCallback(
    e => {
      meta?.downloadOrchestration(row);
      e.stopPropagation();
    },
    [meta, row]
  );

  const onSecondButtonClick = useCallback(
    e => {
      meta?.deleteOrchestration(row);
      e.stopPropagation();
    },
    [meta, row]
  );

  const onThirdButtonClick = useCallback(
    e => {
      meta?.viewTaskRuns(row?.original);
      e.stopPropagation();
    },
    [meta, row]
  );

  return (
    <div className="flex flex-row justify-between items-center w-16">
      <Tooltip aria-label="Download orchestration zip file">
        <Button sx={buttonStyle} onClick={onFirstButtonClick}>
        <Icon
            icon={DownloadIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="8"
            width="16"
            height="16"
          />
        </Button>
      </Tooltip>
      <Tooltip aria-label="Delete Workload">
        <Button sx={buttonStyle} onClick={onSecondButtonClick}>
          <Icon
            icon={TrashFillIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="16"
            width="16"
            height="32"
          />
        </Button>
      </Tooltip>
      <Tooltip aria-label="View task runs">
        <Button sx={buttonStyle} onClick={onThirdButtonClick}>
          <Icon
            icon={TerminalIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="8"
            width="16"
            height="16"
          />
        </Button>
      </Tooltip>
    </div>
  );
};

export const OrchestrationAdminTable = ({
  orchestrations = [],
  selectedOrchestrations = [],
  setSelectedOrchestrations = () => {},
  deleteOrchestration = () => {},
  viewTaskRuns = () => {},
  downloadOrchestration = () => {},
  onOrchestrationClick = () => {},
  hideCheckboxes = false,
  isSelectionColumnChecked = false,
  setSelectionColumnChecked = () => {},
  selectedPage = 1,
  resultsPerPage = 25,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: NameCell,
        width: 160,
      },
      {
        Header: 'Workspace',
        accessor: 'workspace_name',
        Cell: WorkspaceCell,
        width: 160,
      },
      {
        Header: 'Active Tasks',
        accessor: 'numberOfActiveTasks',
        Cell: TasksCell,
        width: 80,
      },
      {
        Header: 'Storage Size',
        accessor: 'storageSize',
        Cell: StorageSizeCell,
        width: 80,
      },
      {
        Header: 'File Reference',
        accessor: 'sha',
        Cell: FileHashCell,
        width: 140,
        tooltipText:
          'Identifier for a stored artifact that is shared when the artifact is identical.',
      },
      {
        Header: 'Created',
        accessor: 'created_at',
        Cell: CreatedAtCell,
        width: 140,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: StatusCell,
        width: 80,
      },
      {
        Header: 'Actions',
        Cell: ActionCellRenderer,
        width: 64,
        disableSortBy: true,
      },
    ],
    []
  );

  const defaultSort = {
    id: 'name',
    desc: false,
  };

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  );
  const meta = React.useMemo(
    () => ({
      downloadOrchestration,
      viewTaskRuns,
      deleteOrchestration,
    }),
    [deleteOrchestration, downloadOrchestration, viewTaskRuns]
  );

  const tableRows = transformOrchestrationsToTableRows(orchestrations);

  return (
    <AlternatingRowDataTable
      id="orchestration-admin-table"
      columns={columns}
      data={tableRows}
      defaultColumn={defaultColumn}
      defaultSort={defaultSort}
      meta={meta}
      selectedRowIds={selectedOrchestrations}
      setSelectedRowIds={setSelectedOrchestrations}
      onItemClick={hideCheckboxes ? onOrchestrationClick : null}
      hideCheckboxes={hideCheckboxes}
      height={640}
      pagination={{
        resultsPerPage,
        selectedPage,
      }}
      isSelectionColumnChecked={isSelectionColumnChecked}
      setSelectionColumnChecked={setSelectionColumnChecked}
      useSelectableRows
    />
  );
};

export default OrchestrationAdminTable;
